import {Trans, useLingui} from "@lingui/react/macro";
import {useState} from "react";
import {useNavigate, useParams} from "react-router";
import type {GroupChallengeResponse} from "../../features/auth/client-auth";
import {acceptShareGroupLink} from "../../features/auth/client-auth";
import type {ModelId} from "../../models/db";
import {oneOffSync} from "../../models/db";
import {type GroupId, getGroupRepoLoader} from "../../models/GroupRepo";
import type {UserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import Spinner from "../../shared/ui/Spinner";
import {Col, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import {wsServerHost} from "../../utils/server-hosts";

// Returns the raw values: the hydrated ones assume the sync brought the group.
const syncGroup = async (groupId: GroupId, userRepo: UserRepo, {addMember}: {addMember: boolean}) =>
  getGroupRepoLoader(groupId).withRepo(userRepo, async (groupRepo) => {
    if (addMember) {
      const userVals = userRepo.vals.get();
      groupRepo.Members.set(userVals.id as string as ModelId<"members">, {
        id: userVals.id,
        joinedAt: new Date(),
        name: userVals.name,
      });
    }
    await oneOffSync(`${wsServerHost}/sync/group/${groupId}`, groupRepo.store);
    return groupRepo.store.getValues();
  });

const AcceptButton = ({secret, userRepo}: {secret: string; userRepo: UserRepo}) => {
  const [info, setInfo] = useState<null | GroupChallengeResponse>(null);
  // set when we joined but the group's sync came back empty
  const [pendingGroupId, setPendingGroupId] = useState<GroupId | null>(null);
  const navigate = useNavigate();
  const {t} = useLingui();
  const loadGroup = async (groupId: GroupId, opts: {addMember: boolean}) => {
    const group = await syncGroup(groupId, userRepo, opts);
    if (!group.id || !group.shortId) {
      // We're a member now, so the invite can't be accepted again. Don't write an entry without a
      // shortId (it would link to /g/undefined), let the user retry the sync instead.
      setPendingGroupId(groupId);
      return;
    }
    userRepo.Group.set(groupId, {
      id: group.id as GroupId,
      shortId: group.shortId,
      name: group.name ?? "",
      joinedAt: new Date(),
    });
    navigate(`/g/${group.shortId}`, {replace: true});
  };
  const handleAccept = async () => {
    const val = await acceptShareGroupLink(userRepo, secret);
    setInfo(val);
    if (val.ok) {
      await loadGroup(val.groupId, {addMember: true});
    }
  };
  if (!info) {
    return (
      <Col rounded="4" surface pa="16" sp="12" align="center">
        <Button onClick={handleAccept}>
          <Trans>Accept invite to group</Trans>
        </Button>
      </Col>
    );
  } else if (pendingGroupId) {
    return (
      <Col rounded="4" surface pa="16" sp="12" align="center">
        <Text as="h2" size="20">
          <Trans>Joined, but the group hasn't loaded yet</Trans>
        </Text>
        <Button onClick={async () => loadGroup(pendingGroupId, {addMember: false})}>
          <Trans>Try again</Trans>
        </Button>
      </Col>
    );
  } else if (info.ok) {
    return <Spinner size="48" />;
  } else {
    const getErrorMessage = () => {
      switch (info.errorKey) {
        case "notFound":
          return t`Link not valid`;
        case "expired":
          return t`Group link expired`;
        case "alreadyAccess":
          return t`You already have access to this group`;
        default:
          return info.errorKey;
      }
    };
    return (
      <Col rounded="4" surface pa="16" sp="12" align="center">
        <Text as="h2" size="20">
          {getErrorMessage()}
        </Text>
        <Text>
          <Trans>Please try again.</Trans>
        </Text>
      </Col>
    );
  }
};

const AcceptGroupInvite = ({userRepo}: {userRepo: UserRepo}) => {
  const {secret} = useParams<{secret: string}>();
  return (
    <AppShell>
      <Col px="12" sp="24" py="24">
        <Text as="h1">
          <Trans>Join Group</Trans>
        </Text>
        <AcceptButton secret={secret || ""} userRepo={userRepo} />
      </Col>
    </AppShell>
  );
};

export default AcceptGroupInvite;
